import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';

import {
  Button,
  Caption,
  Heading,
  Icon,
  SidebarTrigger,
  Stack,
  Tag,
  Text,
  cn,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import {
  type OfferingParam,
  TYPE_META,
  categoryName,
  findOffering,
} from './care-library-data';
import { useCareLibrary } from './care-library-fhir';
import { OfferingMark, OfferingTypeTag } from './offering-types';

const detailFrame =
  'col-span-2 row-span-3 flex h-full min-h-0 flex-col overflow-hidden bg-bg-primary';

/** Type-scoped parameter grid — a hairline-separated label/value table. */
const ParamsGrid: React.FC<{ params: OfferingParam[] }> = ({ params }) => (
  <div className="grid grid-cols-[repeat(auto-fill,minmax(7.5rem,1fr))] gap-px overflow-hidden rounded-xl border border-border-primary bg-border-primary">
    {params.map((p) => (
      <div key={p.label} className="bg-bg-primary p-3">
        <Caption size="3xs" color="supporting">
          {p.label}
        </Caption>
        <Text
          size="md"
          color={p.value === '—' ? 'supporting' : 'primary'}
          className={cn('mt-1', p.mono && 'font-mono')}
        >
          {p.value}
        </Text>
      </div>
    ))}
  </div>
);

const SideCard: React.FC<{
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, action, children }) => (
  <div className="rounded-xl border border-border-primary bg-bg-primary p-4">
    <Stack direction="horizontal" align="center" className="mb-3">
      <Caption size="3xs" color="tertiary" className="flex-1">
        {title}
      </Caption>
      {action}
    </Stack>
    {children}
  </div>
);

/** Outer frame + breadcrumb for the not-found / loading / error / disabled
 *  states. */
const DetailShell: React.FC<{ backTo: string; children: React.ReactNode }> = ({
  backTo,
  children,
}) => {
  const { t } = useTranslation();
  return (
    <div className={detailFrame}>
      <Helmet>
        <title>Care Library | Eluve</title>
      </Helmet>
      <Stack
        direction="horizontal"
        gap={2}
        align="center"
        className="border-b border-border-primary px-6 py-3"
      >
        <SidebarTrigger />
        <Link
          to={backTo}
          className="inline-flex items-center gap-1 text-sm text-text-tertiary hover:text-text-secondary"
        >
          <Icon name="ChevronLeft" size="sm" />
          {t('sidebar.careLibrary')}
        </Link>
      </Stack>
      {children}
    </div>
  );
};

const CenteredNotice: React.FC<{ text: string; backTo?: string }> = ({
  text,
  backTo,
}) => {
  const { t } = useTranslation();
  return (
    <Stack gap={3} align="center" className="px-6 py-24 text-center">
      <Icon name="Layers" size="xl" className="text-text-disabled" />
      <Text color="tertiary">{text}</Text>
      {backTo && (
        <Link to={backTo}>
          <Button variant="outline">
            <Icon name="ChevronLeft" size="xs" />
            {t('careLibrary.detail.backToLibrary')}
          </Button>
        </Link>
      )}
    </Stack>
  );
};

export const CareLibraryDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { tenantId, offeringId } = useParams<{
    tenantId: string;
    offeringId: string;
  }>();
  const { org, isError, enabled } = useCareLibrary();
  const backTo = `/organizations/${tenantId}/care-library`;

  if (isError) {
    return (
      <DetailShell backTo={backTo}>
        <CenteredNotice text={t('careLibrary.detail.loadError')} />
      </DetailShell>
    );
  }
  if (!enabled && !org) {
    return (
      <DetailShell backTo={backTo}>
        <CenteredNotice text={t('careLibrary.page.medplumRequired')} />
      </DetailShell>
    );
  }
  if (!org) {
    return (
      <DetailShell backTo={backTo}>
        <CenteredNotice text={t('careLibrary.detail.loadingOffering')} />
      </DetailShell>
    );
  }

  const offering = offeringId ? findOffering(org, offeringId) : undefined;
  if (!offering) {
    return (
      <DetailShell backTo={backTo}>
        <CenteredNotice
          text={t('careLibrary.detail.notFound')}
          backTo={backTo}
        />
      </DetailShell>
    );
  }

  const meta = TYPE_META[offering.type];
  const params = offering.params;
  const instructions = offering.instructions;
  const totalCats = Object.values(offering.cats).reduce(
    (n, ids) => n + ids.length,
    0,
  );
  const facetGroups = org.facets
    .map((facet) => ({ facet, cats: offering.cats[facet.id] ?? [] }))
    .filter((g) => g.cats.length > 0);

  return (
    <div className={detailFrame}>
      <Helmet>
        <title>{offering.name} | Care Library</title>
      </Helmet>

      {/* Breadcrumb + actions */}
      <Stack
        direction="horizontal"
        gap={2}
        align="center"
        className="border-b border-border-primary px-6 py-3"
      >
        <SidebarTrigger />
        <Link
          to={backTo}
          className="inline-flex items-center gap-1 text-sm text-text-tertiary hover:text-text-secondary"
        >
          <Icon name="ChevronLeft" size="sm" />
          {t('sidebar.careLibrary')}
        </Link>
        <Text inline color="supporting">
          /
        </Text>
        <Text inline size="sm" color="secondary" weight="medium">
          {meta.short}
        </Text>
      </Stack>

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="grid grid-cols-[1fr_320px] gap-6 p-6">
          {/* Main column */}
          <div className="min-w-0">
            <Stack direction="horizontal" gap={4} align="start" fullWidth>
              <OfferingMark type={offering.type} size="lg" />
              <div className="min-w-0 flex-1">
                <Stack
                  direction="horizontal"
                  gap={2}
                  align="center"
                  wrap
                  fullWidth={false}
                >
                  <OfferingTypeTag type={offering.type} />
                  <Text inline size="xs" color="tertiary">
                    {meta.kind}
                  </Text>
                </Stack>
                <Heading size="2xl" level={1} className="mt-2">
                  {offering.name}
                </Heading>
                <Stack
                  direction="horizontal"
                  gap={2.5}
                  align="center"
                  wrap
                  fullWidth={false}
                  className="mt-1.5"
                >
                  {offering.code ? (
                    <Text
                      inline
                      size="xs"
                      color="tertiary"
                      className="font-mono"
                    >
                      {offering.code}
                    </Text>
                  ) : (
                    <Text inline size="xs" color="supporting">
                      {t('careLibrary.detail.noClinicalCode')}
                    </Text>
                  )}
                </Stack>
              </div>
            </Stack>

            <Text color="secondary" className="mt-4 max-w-2xl leading-relaxed">
              {offering.desc}
            </Text>

            {params.length > 0 && (
              <div className="mt-6">
                <Stack
                  direction="horizontal"
                  gap={2}
                  align="center"
                  className="mb-2"
                >
                  <Caption inline size="3xs" color="tertiary">
                    {t('careLibrary.detail.parameters')}
                  </Caption>
                  <Text inline size="xs" color="supporting">
                    {t('careLibrary.detail.parametersNote', {
                      type: meta.short.toLowerCase(),
                    })}
                  </Text>
                </Stack>
                <ParamsGrid params={params} />
              </div>
            )}

            {instructions && (
              <div className="mt-6">
                <Caption size="3xs" color="tertiary" className="mb-2">
                  {t('careLibrary.detail.patientInstructions')}
                </Caption>
                <div className="rounded-xl border border-border-primary bg-bg-primary p-4">
                  <Text color="secondary" className="leading-relaxed">
                    {instructions}
                  </Text>
                </div>
              </div>
            )}
          </div>

          {/* Side column */}
          <div className="flex min-w-0 flex-col gap-4">
            <SideCard title={t('careLibrary.detail.categoriesTitle')}>
              {facetGroups.map((g) => (
                <div key={g.facet.id} className="mb-3">
                  <Caption size="3xs" color="supporting" className="mb-1.5">
                    {g.facet.name}
                  </Caption>
                  <Stack direction="horizontal" gap={1.5} wrap fullWidth>
                    {g.cats.map((cid) => (
                      <Tag
                        key={cid}
                        size="xs"
                        color="gray"
                        label={categoryName(org, g.facet.id, cid)}
                      />
                    ))}
                  </Stack>
                </div>
              ))}
              <Text size="xs" color="tertiary" className="leading-snug">
                {t('careLibrary.detail.belongsToPrefix')}{' '}
                <span className="font-mono text-text-primary">{totalCats}</span>{' '}
                {t('careLibrary.detail.belongsToSuffix')}
              </Text>
            </SideCard>

            <SideCard title={t('careLibrary.detail.usedInPlansTitle')}>
              <Text size="xs" color="tertiary" className="leading-snug">
                {t('careLibrary.detail.usedInPlansEmpty')}
              </Text>
              <Stack
                direction="horizontal"
                gap={2}
                align="start"
                fullWidth
                className="mt-3 rounded-lg border border-border-primary bg-bg-secondary p-2.5"
              >
                <Icon
                  name="Link"
                  size="xs"
                  className="mt-0.5 shrink-0 text-text-tertiary"
                />
                <Text size="xs" color="tertiary" className="leading-snug">
                  {t('careLibrary.detail.reuseByReference')}
                </Text>
              </Stack>
            </SideCard>
          </div>
        </div>
      </div>
    </div>
  );
};
